import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { portalApi, tournamentApi } from '../../services/api'
import MapPicker from '../../components/MapPicker'
import PortalShell from './PortalShell'
import { isOnboardingComplete } from './onboardingStatus'
import { useLanguage } from '../../context/LanguageContext'

/**
 * Course location setup page.
 * Single map picker that auto-fills address/city/province/country via
 * geocoding. Accessible from the dashboard setup checklist.
 */
export default function LocationSetup() {
  const { t } = useLanguage()
  const navigate = useNavigate()
  const [course, setCourse] = useState(null)
  const [latLng, setLatLng] = useState(null)
  const [resolved, setResolved] = useState({
    street: '',
    city: '',
    province: '',
    provinceId: '',
    country: '',
    countryId: null,
    countryCode: null,
    postalCode: '',
  })
  const [countries, setCountries] = useState([])
  const [provinces, setProvinces] = useState([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [status, setStatus] = useState(null)
  const [packages, setPackages] = useState([])

  // Load the existing course + onboarding status + packages (for the step label)
  useEffect(() => {
    Promise.all([
      portalApi.get('/onboarding/status')
        .then(({ data }) => {
          const c = data.course
          setCourse(c)
          setStatus(data)
          if (c?.Latitude && c?.Longitude) {
            setLatLng({ lat: Number(c.Latitude), lng: Number(c.Longitude) })
          }
          setResolved(r => ({
            ...r,
            street: c?.Address1 || '',
            city: c?.City || '',
            provinceId: c?.Province_ID || '',
            countryId: c?.Country_ID || null,
            postalCode: c?.PostalCode || '',
          }))
        })
        .catch(() => {}),
      tournamentApi.get('/play-stay/packages')
        .then(({ data }) => setPackages(Array.isArray(data) ? data : []))
        .catch(() => setPackages([])),
    ]).finally(() => setLoading(false))
  }, [])

  // Load countries once
  useEffect(() => {
    portalApi.get('/lookup/countries').then(({ data }) => setCountries(data)).catch(() => {})
  }, [])

  // Load provinces whenever country changes
  useEffect(() => {
    if (!resolved.countryId) { setProvinces([]); return }
    portalApi.get('/lookup/provinces', { params: { countryId: resolved.countryId } })
      .then(({ data }) => setProvinces(data))
      .catch(() => setProvinces([]))
  }, [resolved.countryId])

  // Match province by name when provinces load
  useEffect(() => {
    if (!resolved.province || !provinces.length) return
    const match = provinces.find(p => p.name.toLowerCase() === resolved.province.toLowerCase())
    if (match && resolved.provinceId !== match.id) {
      setResolved(r => ({ ...r, provinceId: match.id }))
    }
  }, [provinces, resolved.province])

  function handleMapChange({ lat, lng, address }) {
    setLatLng({ lat, lng })
    if (!address) return

    const countryIso = address.address?.countryCode
    const countryRow = countries.find(c => c.iso === countryIso)
    const newCountryId = countryRow?.id || resolved.countryId
    const newCountryName = countryRow?.name || address.address?.country || ''
    const countryChanged = newCountryId !== resolved.countryId

    setResolved(r => ({
      ...r,
      street: address.address?.line1 || '',
      city: address.address?.city || '',
      province: address.address?.state || '',
      provinceId: countryChanged ? '' : r.provinceId,
      country: newCountryName,
      countryId: newCountryId,
      countryCode: countryIso,
      postalCode: address.address?.postalCode || '',
    }))
  }

  const initialSearchQuery = useMemo(() => {
    if (course?.Latitude && course?.Longitude) return ''
    const parts = []
    if (course?.CourseName) parts.push(course.CourseName)
    if (course?.Address1) parts.push(course.Address1)
    if (course?.City) parts.push(course.City)
    return parts.join(', ')
  }, [course])

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    if (!latLng?.lat || !latLng?.lng) {
      return setError(t('sp_location_set_pin', 'Please search or click on the map to set your location.'))
    }
    if (!resolved.city) {
      return setError(t('sp_location_no_city', 'We could not detect your city from the map. Try a more specific location.'))
    }
    if (!resolved.provinceId) {
      return setError(t('sp_location_no_province', 'We could not match your province/state. Try a more specific location.'))
    }
    if (!resolved.countryId) {
      return setError(t('sp_location_no_country', 'We could not match your country. Try a more specific location.'))
    }

    setSubmitting(true)
    try {
      await portalApi.post('/onboarding/location', {
        address: resolved.street,
        city: resolved.city,
        provinceId: Number(resolved.provinceId),
        countryId: Number(resolved.countryId),
        postalCode: resolved.postalCode,
        latitude: latLng.lat,
        longitude: latLng.lng,
      })
      navigate('/portal')
    } catch (err) {
      setError(err.response?.data?.message || t('sp_location_save_failed', 'Failed to save location'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <PortalShell>
      <div style={{ maxWidth: 900, margin: '40px auto', padding: '0 20px' }}>
        <div style={{ marginBottom: 24 }}>
          {!isOnboardingComplete(status, packages) && (
            <div style={{ fontSize: 11, letterSpacing: 2, color: 'var(--brand-600)', fontWeight: 700, marginBottom: 6 }}>
              {t('sp_setup_step_1_location_caps', 'SETUP · STEP 1 OF 4 · LOCATION')}
            </div>
          )}
          <h1 style={{ margin: 0, fontSize: 28, color: 'var(--secondary-950)' }}>
            {t('sp_location_h1', 'Where is your course?')}
          </h1>
          <p style={{ margin: '8px 0 0', color: 'var(--text-muted)', fontSize: 15 }}>
            {t('sp_location_blurb', 'Search for your course or address, then drag the pin to the exact spot. We\'ll fill in the rest automatically. This is used to find hotels near your course.')}
          </p>
        </div>

        {loading ? (
          <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
            {t('loading_ellipsis', 'Loading...')}
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && (
              <div style={{
                padding: '10px 14px', marginBottom: 16,
                background: '#fef2f2', color: '#dc2626',
                border: '1px solid #fecaca', borderRadius: 8, fontSize: 13,
              }}>
                {error}
              </div>
            )}

            <MapPicker
              lat={latLng?.lat || null}
              lng={latLng?.lng || null}
              searchQuery={initialSearchQuery}
              onChange={handleMapChange}
              height={440}
            />

            {latLng && (
              <div style={{
                marginTop: 16, padding: 16,
                background: resolved.city && resolved.provinceId && resolved.countryId ? 'var(--brand-50)' : '#fffbeb',
                border: '1px solid ' + (resolved.city && resolved.provinceId && resolved.countryId ? 'var(--brand-200)' : '#fde68a'),
                borderRadius: 10,
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: 'var(--text-muted)', marginBottom: 8 }}>
                  {t('sp_will_be_saved_as_caps', 'WILL BE SAVED AS')}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 14 }}>
                  <div><strong>{t('sp_address_label', 'Address:')}</strong> {resolved.street || <em style={{ color: '#999' }}>{t('sp_none_detected', 'none detected')}</em>}</div>
                  <div><strong>{t('sp_postal_label', 'Postal:')}</strong> {resolved.postalCode || <em style={{ color: '#999' }}>{t('sp_none_detected', 'none detected')}</em>}</div>
                  <div><strong>{t('sp_city_label', 'City:')}</strong> {resolved.city || <em style={{ color: '#dc2626' }}>{t('sp_not_detected', 'not detected')}</em>}</div>
                  <div><strong>{t('sp_province_state_label', 'Province/State:')}</strong> {resolved.province || <em style={{ color: '#dc2626' }}>{t('sp_not_detected', 'not detected')}</em>}</div>
                  <div style={{ gridColumn: '1 / -1' }}><strong>{t('sp_country_label', 'Country:')}</strong> {resolved.country || <em style={{ color: '#dc2626' }}>{t('sp_not_detected', 'not detected')}</em>}</div>
                </div>
                {(!resolved.city || !resolved.provinceId || !resolved.countryId) && (
                  <div style={{ marginTop: 10, fontSize: 12, color: '#92400e' }}>
                    {t('sp_some_fields_undetected', 'Some fields couldn\'t be detected. Try dragging the pin to a more specific location.')}
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
              <button type="button" onClick={() => navigate('/portal')} className="btn btn-outline-dark" style={{ padding: '12px 24px' }}>
                {t('cancel', 'Cancel')}
              </button>
              <button
                type="submit"
                disabled={submitting || !latLng}
                className="btn btn-primary"
                style={{ padding: '14px 32px', opacity: (submitting || !latLng) ? 0.6 : 1 }}
              >
                {submitting ? t('sp_saving', 'Saving...') : t('sp_save_and_continue', 'Save & Continue')}
              </button>
            </div>
          </form>
        )}
      </div>
    </PortalShell>
  )
}
